import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { useWriterComponentState } from "hooks";

import { CancelShipmentBookingModalContent } from "./CancelShipmentBookingModalContent";
import { ClientAppEvent } from "../../constants";

interface Props {
  onModalClose: VoidFunction;
  modalOpen: boolean;
  shipmentId: number;
  onBookingCancelledSuccess?: VoidFunction | null;
}

export function CancelShipmentBookingModal(props: Props) {
  const {
    shipmentId,
    modalOpen,
    onModalClose,
    onBookingCancelledSuccess = null,
  } = props;

  const posthog = usePostHog();
  const [cancellationReason, setCancellationReason] = useState<string>("");

  const { loading, write, error } = useWriterComponentState({
    errorMessage:
      "Failed to cancel booking. Please contact us if the issue persists",
    writeConfig: {
      method: "post",
      url: `/shipments/${shipmentId}/cancel-booking`,
      body: {
        reason: cancellationReason,
      },
    },
  });

  const submit = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.SHIPMENT_BOOKING_CANCELLED, {
        shipment_id: shipmentId,
        cancellation_reason: cancellationReason,
      });
      if (onBookingCancelledSuccess) {
        onBookingCancelledSuccess();
      }
      onModalClose();
    });
  };

  return (
    <Dialog fullWidth open={modalOpen} onClose={onModalClose}>
      <DialogTitle component="div">
        <Typography variant="h4">Cancel Booking</Typography>
      </DialogTitle>
      <DialogContent>
        <CancelShipmentBookingModalContent
          cancellationReason={cancellationReason}
          onCancellationUpdate={(newValue: string) =>
            setCancellationReason(newValue)
          }
          loading={loading}
          error={error}
        />
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Button autoFocus variant="contained" onClick={onModalClose}>
            Go Back
          </Button>
          <Button
            onClick={submit}
            disabled={cancellationReason === ""}
            color="error"
          >
            {!error ? "Cancel Booking" : "retry"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
